import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { LicitacoesFiltrosPersistiveis } from './useLicitacoes';

/**
 * `buscas_salvas.filtros` é um JSONB livre: guardamos o conjunto completo de
 * filtros da busca avançada (geografia, modalidades, órgão, esfera e os
 * diferenciais de IA), exceto a paginação. O job `alertas-licitacoes` reusa o
 * mesmo builder do feed, então tudo o que a tela filtra também alerta.
 */
export type BuscaSalvaFiltros = LicitacoesFiltrosPersistiveis;

export interface BuscaSalva {
  id: number;
  empresa_id: number;
  nome: string;
  filtros: BuscaSalvaFiltros;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

interface BuscasSalvasResponse {
  success: boolean;
  data: { buscas: BuscaSalva[] };
}

interface BuscaSalvaResponse {
  success: boolean;
  data: { busca: BuscaSalva };
}

export interface AlertaLicitacao {
  id: number;
  lida: boolean;
  created_at: string;
  score: number | null;
  busca_salva_id: number;
  busca_nome: string;
  empresa_id: number;
  licitacao_id: number;
  objeto: string;
  orgao: string | null;
  uf: string | null;
  municipio: string | null;
  portal: string;
  link: string | null;
  prazo_abertura: string | null;
  valor_estimado: number | null;
}

interface AlertasResponse {
  success: boolean;
  data: { alertas: AlertaLicitacao[]; total_nao_lidos: number };
}

export function useBuscasSalvas(empresaId?: number) {
  return useQuery({
    queryKey: ['buscas-salvas', empresaId],
    queryFn: async () => {
      const params: Record<string, number> = {};
      if (empresaId) params.empresa_id = empresaId;
      const response = await api.get<BuscasSalvasResponse>('/buscas-salvas', { params });
      return response.data.buscas;
    },
  });
}

export function useCriarBuscaSalva() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { empresa_id: number; nome: string; filtros: BuscaSalvaFiltros }) => {
      const response = await api.post<BuscaSalvaResponse>('/buscas-salvas', payload);
      return response.data.busca;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buscas-salvas'] });
    },
  });
}

export function useAtualizarBuscaSalva() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: number; nome?: string; filtros?: BuscaSalvaFiltros; ativo?: boolean }) => {
      const response = await api.put<BuscaSalvaResponse>(`/buscas-salvas/${id}`, payload);
      return response.data.busca;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buscas-salvas'] });
    },
  });
}

export function useExcluirBuscaSalva() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/buscas-salvas/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buscas-salvas'] });
    },
  });
}

export function useAlertasLicitacoes() {
  return useQuery({
    queryKey: ['alertas-licitacoes'],
    queryFn: async () => {
      const response = await api.get<AlertasResponse>('/buscas-salvas/alertas');
      return response.data;
    },
    refetchInterval: 60_000,
  });
}

export function useMarcarAlertaLido() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.patch(`/buscas-salvas/alertas/${id}/lida`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertas-licitacoes'] });
    },
  });
}
